import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, Search, Crown, UserMinus, MoreVertical,
  Loader2, AlertCircle, Check, ChevronRight, User, MessageSquare, ShieldBan, ArrowRightLeft,
} from 'lucide-react';
import { useMessageStore } from '../../../store/messageStore';
import { useServerStore } from '../../../store/serverStore';
import ProfileView from '../../profile/ProfileView';
import { useAuthStore } from '../../../store/authStore';
import { Permissions, computeEffectivePermissions, hasPermission } from '../../../lib/permissions';
import AppBadge from '../../ui/AppBadge';

interface MembersTabProps {
  serverId: string;
  ownerId: string;
  currentUserId: string;
}

interface AssignedRole {
  id: string;
  name: string;
  color: string | null;
  permissions: number;
  position: number;
  is_everyone?: boolean;
  hoist?: boolean;
}

interface ServerRole {
  id: string;
  server_id: string;
  name: string;
  color: string | null;
  permissions: number;
  position: number;
  is_everyone?: boolean;
  hoist?: boolean;
}

interface Member {
  id: string;
  server_id: string;
  user_id: string;
  role: string;
  bot?: boolean;
  roles: AssignedRole[];
  created_at: string;
  user: {
    username: string;
    avatar_url: string | null;
    status: string;
    bot?: boolean;
    is_system?: boolean;
  };
}

const MembersTab: React.FC<MembersTabProps> = ({ serverId, ownerId, currentUserId }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [serverRoles, setServerRoles] = useState<ServerRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [roleSubmenu, setRoleSubmenu] = useState<string | null>(null);
  const [togglingRole, setTogglingRole] = useState<string | null>(null);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ id: string; username: string } | null>(null);
  const [transferConfirmName, setTransferConfirmName] = useState('');

  const { setCurrentDmUser } = useMessageStore();
  const { setCurrentChannel, currentServer, fetchUserServers } = useServerStore();
  const { user: authUser } = useAuthStore();

  const isOwner = currentUserId === ownerId;

  // Compute effective permissions for the current user
  const currentMember = members.find((m) => m.user_id === currentUserId);
  const effectivePerms = computeEffectivePermissions(isOwner, currentMember?.roles || []);
  const canManageRoles = hasPermission(effectivePerms, Permissions.MANAGE_ROLES);
  const canKick = hasPermission(effectivePerms, Permissions.KICK_MEMBERS);
  const canBan = hasPermission(effectivePerms, Permissions.BAN_MEMBERS);
  const canModerate = canManageRoles || canKick || canBan;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const [membersRes, rolesRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}/members`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
      setServerRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [serverId]);

  const toggleRole = async (userId: string, roleId: string, hasRole: boolean) => {
    setTogglingRole(roleId);
    try {
      const token = localStorage.getItem('access_token');
      if (hasRole) {
        await axios.delete(
          `${import.meta.env.VITE_API_URL}/servers/${serverId}/members/${userId}/roles/${roleId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(
          `${import.meta.env.VITE_API_URL}/servers/${serverId}/members/${userId}/roles/${roleId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update role');
    } finally {
      setTogglingRole(null);
    }
  };

  const kickMember = async (userId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/members/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove member');
    }
    setOpenDropdown(null);
    setRoleSubmenu(null);
  };

  const banMember = async (userId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/bans`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to ban member');
    }
    setOpenDropdown(null);
    setRoleSubmenu(null);
  };

  const transferOwnership = async (userId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/transfer-ownership`,
        { newOwnerId: userId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchData();
      await fetchUserServers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to transfer ownership');
    }
    setTransferTarget(null);
    setTransferConfirmName('');
  };

  const openProfile = (userId: string) => {
    setOpenDropdown(null);
    setRoleSubmenu(null);
    setProfileUserId(userId);
  };

  const sendMessage = (userId: string) => {
    setOpenDropdown(null);
    setRoleSubmenu(null);
    setCurrentChannel(null);
    setCurrentDmUser(userId);
  };

  const filtered = members.filter((m) =>
    m.user.username.toLowerCase().includes(search.toLowerCase()),
  );

  // Sort: owner first, then by username
  const sorted = [...filtered].sort((a, b) => {
    if (a.user_id === ownerId) return -1;
    if (b.user_id === ownerId) return 1;
    return a.user.username.localeCompare(b.user.username);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Members</h2>
          <p className="text-xs text-text-muted mt-0.5">
            {members.length} member{members.length !== 1 ? 's' : ''} in this server.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="settings-input pl-9"
          placeholder="Search members..."
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-accent-color" />
        </div>
      ) : (
        <div className="divide-y divide-border-color/50">
          {sorted.map((member) => {
            const isMemberOwner = member.user_id === ownerId;
            const isCurrentMember = member.user_id === currentUserId;
            const isBot = Boolean(member.bot || member.user?.bot || member.user?.is_system);
            const memberRoleIds = new Set((member.roles || []).map((r) => r.id));
            const canToggleRolesForMember = canManageRoles && ((!isCurrentMember && !isMemberOwner) || (isMemberOwner && isOwner));
            const canUseModerationActions = canModerate && !isMemberOwner;
            const showActions = !isCurrentMember || canToggleRolesForMember;

            return (
              <div
                key={member.id}
                className={`px-4 py-3 flex items-center justify-between group/row hover:bg-highlight/20 transition-all ${
                  openDropdown === member.id ? 'relative z-[99]' : ''
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 rounded-lg bg-bg-tertiary border border-border-color overflow-hidden">
                      {member.user.avatar_url ? (
                        <img src={member.user.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm font-bold text-text-muted">
                          {member.user.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    {/* Status dot */}
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-bg-primary ${
                      member.user.status === 'online' ? 'bg-success' :
                      member.user.status === 'idle' ? 'bg-yellow-400' :
                      member.user.status === 'dnd' ? 'bg-error' :
                      'bg-text-muted'
                    }`} />
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary truncate">
                        {member.user.username}
                      </span>
                      {isMemberOwner && (
                        <Crown className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" title="Server Owner" />
                      )}
                      {isBot && <AppBadge />}
                    </div>
                    {/* Role badges */}
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {(member.roles || [])
                        .filter((role) => !role.is_everyone)
                        .sort((a, b) => b.position - a.position)
                        .map((role) => (
                          <span
                            key={role.id}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border"
                            style={{
                              color: role.color || undefined,
                              backgroundColor: role.color ? `${role.color}15` : undefined,
                              borderColor: role.color ? `${role.color}30` : undefined,
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: role.color || '#99AAB5' }} />
                            {role.name}
                          </span>
                        ))}
                      {isBot && (
                        <span className="text-[10px] text-text-muted">Installed app</span>
                      )}
                      {!isBot && !isMemberOwner && (!member.roles || member.roles.length === 0) && (
                        <span className="text-[10px] text-text-muted">No roles</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {showActions && (
                  <div className="relative">
                    <button
                      onClick={() => {
                        setOpenDropdown(openDropdown === member.id ? null : member.id);
                        setRoleSubmenu(null);
                      }}
                      className="p-1.5 rounded-lg hover:bg-highlight/50 text-text-secondary hover:text-text-primary transition-all opacity-0 group-hover/row:opacity-100"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {openDropdown === member.id && (
                      <>
                        <div className="fixed inset-0 z-[100]" onClick={() => { setOpenDropdown(null); setRoleSubmenu(null); }} />
                        <div className="absolute right-0 top-full mt-1 z-[101] w-56 bg-bg-primary border border-border-color rounded-xl shadow-xl overflow-hidden">
                          {/* Profile & Message */}
                          <div className="p-1">
                            <button
                              onClick={() => openProfile(member.user_id)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg text-text-secondary hover:text-text-primary hover:bg-highlight/50 transition-all"
                            >
                              <User className="w-3.5 h-3.5" />
                              Profile
                            </button>
                            {!isCurrentMember && (
                              <button
                                onClick={() => sendMessage(member.user_id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg text-text-secondary hover:text-text-primary hover:bg-highlight/50 transition-all"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                Send Message
                              </button>
                            )}
                            {isOwner && !isMemberOwner && !isBot && (
                              <button
                                onClick={() => {
                                  setTransferTarget({ id: member.user_id, username: member.user.username });
                                  setOpenDropdown(null);
                                  setRoleSubmenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg text-amber-400 hover:bg-amber-400/10 transition-all"
                              >
                                <ArrowRightLeft className="w-3.5 h-3.5" />
                                Transfer Ownership
                              </button>
                            )}
                          </div>

                          {/* Moderation (requires permissions, not on owner) */}
                          {(canToggleRolesForMember || canUseModerationActions) && (
                            <>
                              <div className="border-t border-border-color" />
                              <div className="p-1">
                                {/* Roles submenu */}
                                {canToggleRolesForMember && (
                                <div className="relative">
                                  <button
                                    onClick={() => setRoleSubmenu(roleSubmenu === member.id ? null : member.id)}
                                    className="w-full flex items-center justify-between px-3 py-2 text-xs rounded-lg text-text-secondary hover:text-text-primary hover:bg-highlight/50 transition-all"
                                  >
                                    <span className="flex items-center gap-2">
                                      <Users className="w-3.5 h-3.5" />
                                      Roles
                                    </span>
                                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${roleSubmenu === member.id ? 'rotate-90' : ''}`} />
                                  </button>

                                  {roleSubmenu === member.id && (
                                    <div className="mx-1 mb-1 p-1 bg-bg-tertiary/50 rounded-lg">
                                      {serverRoles.length > 0 ? (
                                        serverRoles
                                          .filter((role) => !role.is_everyone)
                                          .sort((a, b) => b.position - a.position)
                                          .map((role) => {
                                            const hasRole = memberRoleIds.has(role.id);
                                            const isToggling = togglingRole === role.id;
                                            return (
                                              <button
                                                key={role.id}
                                                onClick={() => toggleRole(member.user_id, role.id, hasRole)}
                                                disabled={isToggling}
                                                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md hover:bg-highlight/50 transition-all disabled:opacity-50"
                                              >
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                                                  hasRole
                                                    ? 'border-accent-color bg-accent-color'
                                                    : 'border-border-color bg-bg-tertiary'
                                                }`}>
                                                  {hasRole && <Check className="w-2.5 h-2.5 text-black" />}
                                                </div>
                                                <span
                                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                                  style={{ backgroundColor: role.color }}
                                                />
                                                <span className="text-text-primary truncate">{role.name}</span>
                                              </button>
                                            );
                                          })
                                      ) : (
                                        <p className="px-2.5 py-2 text-[10px] text-text-muted text-center">
                                          No roles created yet. Create roles in the Roles tab.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                                )}

                                {canUseModerationActions && canKick && (
                                <button
                                  onClick={() => kickMember(member.user_id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg text-text-secondary hover:text-text-primary hover:bg-highlight/50 transition-all"
                                >
                                  <UserMinus className="w-3.5 h-3.5" />
                                  {isBot ? 'Remove App' : 'Kick'}
                                </button>
                                )}
                                {canUseModerationActions && canBan && !isBot && (
                                <button
                                  onClick={() => banMember(member.user_id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg text-error hover:bg-error/10 transition-all"
                                >
                                  <ShieldBan className="w-3.5 h-3.5" />
                                  Ban
                                </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-text-muted">No members found.</p>
            </div>
          )}
        </div>
      )}

      {/* Profile modal */}
      {profileUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200]" onClick={() => setProfileUserId(null)}>
          <div className="relative w-80" onClick={e => e.stopPropagation()}>
            <ProfileView
              userId={profileUserId}
              isCurrentUser={profileUserId === authUser?.id}
              onClose={() => setProfileUserId(null)}
            />
          </div>
        </div>
      )}

      {/* Transfer ownership confirmation modal */}
      {transferTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200]" onClick={() => { setTransferTarget(null); setTransferConfirmName(''); }}>
          <div className="bg-bg-primary border border-border-color rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text-primary mb-2">Transfer Ownership</h3>
            <p className="text-sm text-text-secondary mb-4">
              Are you sure you want to transfer ownership of <span className="font-semibold text-text-primary">{currentServer?.name}</span> to <span className="font-semibold text-text-primary">{transferTarget.username}</span>?
              This action is <span className="text-error font-semibold">irreversible</span>.
            </p>
            <p className="text-xs text-text-muted mb-2">
              Type the server name to confirm:
            </p>
            <input
              type="text"
              value={transferConfirmName}
              onChange={(e) => setTransferConfirmName(e.target.value)}
              placeholder={currentServer?.name || ''}
              className="settings-input mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setTransferTarget(null); setTransferConfirmName(''); }}
                className="px-4 py-2 text-sm rounded-lg text-text-secondary hover:text-text-primary hover:bg-highlight/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => transferOwnership(transferTarget.id)}
                disabled={transferConfirmName !== currentServer?.name}
                className="px-4 py-2 text-sm rounded-lg bg-error text-white hover:bg-error/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersTab;
